import { styled } from "@linaria/react";
import type { FC } from "react";
import type { QuizQuestionWithPartsResponse } from "@/api/quiz.ts";
import { buildQuestionPartsModel, type QuestionContentPart } from "@/helpers/questionParts.ts";
import { QUESTION_IMAGE_ENDPOINT } from "@/constants/settings.ts";
import { t } from "@/themes/theme.ts";
import { RenderTutorParityLatex } from "@/helpers/latex-tutor.tsx";

interface QuestionPartsContentProps {
  compact?: boolean;
  question: QuizQuestionWithPartsResponse;
}

export const QuestionPartsContent: FC<QuestionPartsContentProps> = ({
  compact = false,
  question,
}) => {
  const model = buildQuestionPartsModel(question);

  if (!model.parts.length || !model.answerChoices.length) return null;

  return (
    <QuestionCard data-testid="question-parts-card">
      <QuestionStem data-testid="question-parts-stem">
        <QuestionPartList parts={model.parts} imageAltFallback="Question image" />
      </QuestionStem>
      <AnswerList aria-label="Read-only answer choices" data-compact={compact}>
        {model.answerChoices.map((answerChoice) => (
          <AnswerChoice
            data-answer-letter={answerChoice.answerLetter}
            key={answerChoice.answerLetter}
          >
            <AnswerLetter>{answerChoice.answerLetter}</AnswerLetter>
            <QuestionPartList
              parts={answerChoice.parts}
              imageAltFallback={`Answer ${answerChoice.answerLetter} image`}
              isAnswer
            />
          </AnswerChoice>
        ))}
      </AnswerList>
    </QuestionCard>
  );
};

interface QuestionPartListProps {
  imageAltFallback: string;
  isAnswer?: boolean;
  parts: Array<QuestionContentPart>;
}

const QuestionPartList: FC<QuestionPartListProps> = ({
  imageAltFallback,
  isAnswer = false,
  parts,
}) => (
  <PartList>
    {parts.map((part) => (
      <QuestionPart
        imageAltFallback={imageAltFallback}
        isAnswer={isAnswer}
        key={part.partId}
        part={part}
      />
    ))}
  </PartList>
);

interface QuestionPartProps {
  imageAltFallback: string;
  isAnswer: boolean;
  part: QuestionContentPart;
}

const QuestionPart: FC<QuestionPartProps> = ({ imageAltFallback, isAnswer, part }) => {
  if (part.contentType === "Image") {
    if (!part.imagePath?.trim()) return null;

    return (
      <QuestionImage
        alt={part.altText || imageAltFallback}
        data-answer-image={isAnswer || undefined}
        loading="lazy"
        src={getQuestionImageUrl(part.imagePath)}
      />
    );
  }

  const content = part.content.trim();
  if (!content) return null;

  return (
    <TextPart>
      <RenderTutorParityLatex message={content} />
    </TextPart>
  );
};

const getQuestionImageUrl = (imagePath: string) => {
  const path = imagePath.trim();

  if (/^([a-z][a-z\d+\-.]*:)?\/\//i.test(path) || path.startsWith("data:")) {
    return path;
  }

  return `${QUESTION_IMAGE_ENDPOINT}/${path}`.replace(/([^:]\/)\/+/g, "$1");
};

const QuestionCard = styled.section`
  width: 100%;
  max-width: 35rem;
  min-width: 0;
  overflow: hidden;
  border: ${t.brandBorderSegment};
  border-radius: ${t.brandBorderRadius};
  background: ${t.brandNeutralWhite};
  color: ${t.brandNeutralSlate};
`;

const QuestionStem = styled.div`
  padding: 1rem;
  font-size: var(--14px);
  line-height: 1.5rem;
`;

const AnswerList = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.5rem;
  padding: 0.75rem;
  border-top: ${t.brandBorderSegment};

  &[data-compact="true"] {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const AnswerChoice = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
  padding: 0.75rem;
  border: ${t.brandBorderSegment};
  border-radius: ${t.brandBorderRadius};
  background: ${t.brandNeutralCoconut};
  font-size: var(--14px);
  line-height: 1.5rem;
  font-weight: 600;
`;

const AnswerLetter = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  background: ${t.brandFunctional};
  color: ${t.brandNeutralWhite};
  font-size: var(--13px);
  font-weight: 700;
`;

const PartList = styled.div`
  display: grid;
  gap: 0.5rem;
  min-width: 0;
`;

const TextPart = styled.div`
  min-width: 0;
  overflow-wrap: anywhere;

  p {
    margin: 0;
  }
`;

const QuestionImage = styled.img`
  display: block;
  max-width: 100%;
  max-height: 16rem;
  margin: 0 auto;
  object-fit: contain;

  &[data-answer-image="true"] {
    max-height: 6rem;
  }
`;
